import { CheckedAddress, NormalizedNumber } from '@sparkdotfi/common-universal'
import { optimism } from 'viem/chains'

import { SUSDS_POINTS_MULTIPLIER } from '../../../consts'
import { ChainConfigEntry, SavingsStatsItem } from '../../types'
import { sortSavingsAccounts } from '../../utils'
import { chainLogo, tokenLogos } from './assets'
import { createTokenDefinitionRepository } from '@/domain/token-repository/TokenRepository'
import { TokenDefinition } from '@/domain/types/TokenDefinition'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { skySavingsGeneralStatsQueryOptionsFactory } from '@/features/savings/data/general-stats-query/skySavings'
import { skySavingsBackedByOptions } from '@/features/savings/data/savings-charts/backed-by-query/skySavingsBackedByQuery'
import { susdsSavingsRateQueryOptions } from '@/features/savings/data/savings-charts/savings-rate-query/skySavings'
import { ssrAuthOracleConverterQueryOptions } from '@/features/savings/logic/savings-converters/ssrAuthOracleConverter'

const usdc = new TokenDefinition({
  symbol: TokenSymbol('USDC'),
  name: 'Circle USD',
  decimals: 6,
  address: CheckedAddress('0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85'),
  logo: tokenLogos.usdc,
})

const usds = new TokenDefinition({
  symbol: TokenSymbol('USDS'),
  name: 'USDS Stablecoin',
  decimals: 18,
  address: CheckedAddress('0x4F13a96EC5C4Cf34e442b46Bbd98a0791F20edC3'),
  logo: tokenLogos.usds,
})

const susds = new TokenDefinition({
  symbol: TokenSymbol('sUSDS'),
  name: 'Savings USDS',
  decimals: 18,
  address: CheckedAddress('0xb5B2dc7fd34C249F4be7fB1fCea07950784229e0'),
  logo: tokenLogos.susds,
})

const susdc = new TokenDefinition({
  symbol: TokenSymbol('sUSDC'),
  name: 'Savings USDC',
  decimals: 18,
  address: CheckedAddress('0xCF9326e24EBfFBEF22ce1050007A43A3c0B6DB55'),
  logo: tokenLogos.susdc,
})

const op = new TokenDefinition({
  symbol: TokenSymbol('OP'),
  name: 'Optimism',
  decimals: 18,
  address: CheckedAddress('0x4200000000000000000000000000000000000042'),
  logo: tokenLogos.op,
})

export const optimismConfig: ChainConfigEntry = {
  originChainId: optimism.id,
  meta: {
    name: 'Optimism',
    logo: chainLogo,
  },
  tokenDefinitionRepository: createTokenDefinitionRepository([usdc, usds, susds, susdc, op]),
  nativeAssetInfo: {
    nativeAssetName: 'Ether',
    wrappedNativeAssetSymbol: TokenSymbol('WETH'),
    wrappedNativeAssetAddress: CheckedAddress('0x4200000000000000000000000000000000000006'),
    nativeAssetSymbol: TokenSymbol('ETH'),
    minRemainingNativeAssetBalance: NormalizedNumber(0.0005),
    nativeAssetLogo: tokenLogos.eth,
  },
  tokensWithMalformedApprove: [],
  tokensRequiringResetApproval: [],
  contractsRequiringUsdtResetApproval: [],
  permitSupport: {},
  sparkLend: undefined,
  savings: {
    priceConfigs: {
      [usdc.symbol]: { type: 'fixed-usd' },
      [usds.symbol]: { type: 'fixed-usd' },
      [susds.symbol]: { type: 'ssr-auth-oracle' },
      [susdc.symbol]: { type: 'vault', assetsDecimals: 6 },
    },
    accounts: sortSavingsAccounts([
      {
        savingsToken: susdc.symbol,
        underlyingToken: usdc.symbol,
        supportedAssets: [usdc.symbol],
        fetchConverterQuery: ssrAuthOracleConverterQueryOptions,
        savingsRateQueryOptions: susdsSavingsRateQueryOptions,
        stats: {
          queryOptions: skySavingsGeneralStatsQueryOptionsFactory(susdc.symbol),
          items: ['tvl', 'users', 'liquidity-sky-psm'] satisfies SavingsStatsItem[],
        },
        backedByQueryOptions: skySavingsBackedByOptions,
        liquidityChartQueryOptions: undefined,
        sparkPointsMultiplier: undefined,
      },
      {
        savingsToken: susds.symbol,
        underlyingToken: usds.symbol,
        supportedAssets: [usds.symbol, usdc.symbol],
        fetchConverterQuery: ssrAuthOracleConverterQueryOptions,
        savingsRateQueryOptions: susdsSavingsRateQueryOptions,
        stats: {
          queryOptions: skySavingsGeneralStatsQueryOptionsFactory(susds.symbol),
          items: ['tvl', 'users'] satisfies SavingsStatsItem[],
        },
        backedByQueryOptions: skySavingsBackedByOptions,
        liquidityChartQueryOptions: undefined,
        sparkPointsMultiplier: SUSDS_POINTS_MULTIPLIER,
      },
    ]),
    psmStables: [usdc.symbol, usds.symbol],
  },
  legacyFarms: undefined,
  spk: undefined,
  points: undefined,
  sparkRewards: {},
  swap: {
    priceConfigs: {
      [usdc.symbol]: { type: 'fixed-usd' },
      [usds.symbol]: { type: 'fixed-usd' },
      [susds.symbol]: { type: 'ssr-auth-oracle' },
    },
  },
}
